import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db, appId } from '../config/firebase';
import { collection, query, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, where, orderBy, getDocs } from 'firebase/firestore';

const StudentDataContext = createContext();

export const useStudentData = () => {
    const context = useContext(StudentDataContext);
    if (!context) {
        throw new Error('useStudentData must be used within a StudentDataProvider');
    }
    return context;
};

// Function to deduplicate students based on nama and nisn
const deduplicateStudents = (students) => {
    const uniqueStudents = new Map();

    students.forEach(student => {
        const key = `${student.nama || ''}_${student.nisn || ''}`.toLowerCase();

        if (!uniqueStudents.has(key)) {
            uniqueStudents.set(key, student);
        } else {
            // If duplicate found, keep the one with more complete data or newer timestamp
            const existing = uniqueStudents.get(key);
            const isNewer = student.createdAt && existing.createdAt &&
                student.createdAt.seconds > existing.createdAt.seconds;
            const hasMoreData = Object.keys(student).length > Object.keys(existing).length;

            if (isNewer || hasMoreData) {
                console.log(`🔄 Replacing duplicate student: ${student.nama} (${student.nisn})`);
                uniqueStudents.set(key, student);
            } else {
                console.log(`⚠️ Keeping existing student: ${existing.nama} (${existing.nisn})`);
            }
        }
    });

    const result = Array.from(uniqueStudents.values());
    console.log(`📊 Deduplication: ${students.length} → ${result.length} students`);

    return result;
};

export const StudentDataProvider = ({ children }) => {
    const { user } = useAuth();
    const [students, setStudents] = useState([]);
    const [assessmentTypes, setAssessmentTypes] = useState([]);
    const [progress, setProgress] = useState({});
    const [scores, setScores] = useState([]);
    const [schools, setSchools] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [studentsLoaded, setStudentsLoaded] = useState(false);

    // 🔧 PERBAIKAN: Load students from admin collection ONLY (sederhana dan stabil)
    useEffect(() => {
        if (!user) {
            console.log('⚠️ StudentDataContext: No user, skipping students load');
            setStudentsLoaded(true);
            setIsLoading(false);
            return;
        }

        console.log('🔄 StudentDataContext: Loading students for user:', user.uid);
        console.log('🏫 User school:', user.namaSekolah || user.schoolName);
        console.log('🔧 App ID:', appId);

        // Check if user has school information
        if (!user.namaSekolah && !user.schoolName) {
            console.log('⚠️ StudentDataContext: No school information for user');
            setError('Informasi sekolah tidak ditemukan. Silakan hubungi administrator.');
            setStudents([]);
            setStudentsLoaded(true);
            setIsLoading(false);
            return;
        }

        const schoolName = user.namaSekolah || user.schoolName;
        console.log('🎯 Loading students for school:', schoolName);

        // 🔧 PERBAIKAN: Hanya load dari collection admin (sederhana)
        const adminStudentsRef = collection(db, `artifacts/${appId}/siswa`);
        console.log('🔧 Database path:', `artifacts/${appId}/siswa`);

        let unsubscribe = null;

        try {
            console.log('🔄 StudentDataContext: Loading from admin collection...');
            // 🔧 PERBAIKAN: Query sangat sederhana tanpa filter untuk menghindari composite index
            const adminQuery = query(adminStudentsRef);
            console.log('🔧 Query: No filters (client-side filtering)');

            unsubscribe = onSnapshot(adminQuery, (snapshot) => {
                const allStudentsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                console.log('✅ StudentDataContext: All students loaded:', allStudentsList.length);
                console.log('📊 All students data:', allStudentsList);

                // 🔧 PERBAIKAN: Filter dan sort di client-side untuk menghindari composite index
                const filteredStudents = allStudentsList.filter(student => {
                    // Filter berdasarkan status dan sekolah
                    const isValidStatus = student.status !== 'deleted';
                    const isCorrectSchool = student.sekolah === schoolName || student.namaSekolah === schoolName;
                    return isValidStatus && isCorrectSchool;
                });

                console.log('🔍 Filtered students for school:', schoolName, 'Count:', filteredStudents.length);

                if (filteredStudents.length > 0) {
                    // 🔧 PERBAIKAN: Sort manual di client-side untuk menghindari composite index
                    const sortedStudents = filteredStudents.sort((a, b) => {
                        // Sort berdasarkan nama (ascending)
                        const nameA = (a.nama || a.name || '').toLowerCase();
                        const nameB = (b.nama || b.name || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                    });

                    const uniqueStudents = deduplicateStudents(sortedStudents);
                    console.log('🔄 StudentDataContext: After deduplication:', uniqueStudents.length);

                    setStudents(uniqueStudents);
                    setStudentsLoaded(true);
                    setError('');
                    setIsLoading(false);

                    // 🔧 PERBAIKAN: Debug info untuk troubleshooting
                    console.log('🎯 Students successfully loaded for school:', schoolName);
                    console.log('📋 Sample student data:', uniqueStudents[0]);
                } else {
                    console.log('⚠️ No students found in admin collection');
                    console.log('🔍 Debug: Checking if collection exists and has data...');
                    console.log('🏫 Looking for school:', schoolName);
                    console.log('🔧 Database path:', `artifacts/${appId}/siswa`);

                    // 🔧 PERBAIKAN: Debug query tanpa filter untuk melihat semua data
                    getDocs(adminStudentsRef).then(allDocs => {
                        console.log('🔍 All documents in collection:', allDocs.docs.length);
                        allDocs.docs.forEach(doc => {
                            const data = doc.data();
                            console.log('  - Document:', doc.id, 'Data:', {
                                nama: data.nama,
                                sekolah: data.sekolah,
                                status: data.status,
                                namaSekolah: data.namaSekolah,
                                userId: data.userId
                            });
                        });

                        // 🔧 PERBAIKAN: Check if there are students with different school names
                        const differentSchools = [...new Set(allDocs.docs.map(doc => doc.data().sekolah || doc.data().namaSekolah))];
                        console.log('🏫 Different schools found in collection:', differentSchools);

                        if (differentSchools.length > 0) {
                            console.log('💡 Suggestion: Try updating user school info to match one of:', differentSchools);
                        }
                    }).catch(debugError => {
                        console.error('🔍 Debug query error:', debugError);
                    });

                    setStudents([]);
                    setStudentsLoaded(true);
                    setError(`Tidak ada data siswa ditemukan untuk sekolah: ${schoolName}`);
                    setIsLoading(false);
                }
            }, (error) => {
                console.error('❌ StudentDataContext: Error with admin collection query:', error);
                console.log('🔍 Error details:', error.message);
                console.log('🔍 Error code:', error.code);

                setStudents([]);
                setStudentsLoaded(true);
                setError(`Gagal memuat data siswa: ${error.message}`);
                setIsLoading(false);
            });
        } catch (error) {
            console.error('❌ StudentDataContext: Error setting up admin query:', error);
            setStudents([]);
            setStudentsLoaded(true);
            setError('Gagal memuat data siswa');
            setIsLoading(false);
        }

        // Cleanup function
        return () => {
            if (unsubscribe && typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [user]);


    // Load assessment types from user's collection
    useEffect(() => {
        if (!user) return;

        let unsubscribe = null;

        try {
            const assessmentTypesRef = collection(db, `artifacts/${appId}/siswa_assessment_types`);
            // STRICT USER ISOLATION: Only fetch assessment types created by this user
            const q = query(assessmentTypesRef, where('createdBy', '==', user.uid));

            unsubscribe = onSnapshot(q, (snapshot) => {
                const assessmentTypesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                // Sort client-side to avoid complex index requirements
                assessmentTypesList.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
                setAssessmentTypes(assessmentTypesList);
                console.log(`✅ Assessment Types loaded for user ${user.uid}: ${assessmentTypesList.length}`);
            }, (error) => {
                console.error('Error loading assessment types:', error);
                setAssessmentTypes([]);
            });
        } catch (error) {
            console.error('Error setting up assessment types query:', error);
        }

        // Cleanup function
        return () => {
            if (unsubscribe && typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [user]);

    // Load progress from global collection strictly for this user
    useEffect(() => {
        if (!user) return;

        let unsubscribe = null;

        try {
            const progressRef = collection(db, `artifacts/${appId}/siswa_progress`);
            // STRICT USER ISOLATION: Only fetch progress managed by this guru
            const q = query(progressRef, where('guruId', '==', user.uid));

            unsubscribe = onSnapshot(q, (snapshot) => {
                const progressData = {};
                snapshot.forEach(doc => {
                    const data = doc.data();
                    if (data.studentId && data.competencyId) {
                        progressData[`${data.studentId}_${data.competencyId}`] = data.status;
                    }
                });
                setProgress(progressData);
                console.log(`✅ Progress loaded for user ${user.uid}:`, Object.keys(progressData).length);
            }, (error) => {
                console.error('Error loading progress:', error);
            });
        } catch (error) {
            console.error('Error setting up progress query:', error);
        }

        // Cleanup function
        return () => {
            if (unsubscribe && typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [user]);

    // Load scores from user's collection strictly for this user
    useEffect(() => {
        if (!user) return;

        let unsubscribe = null;

        try {
            const scoresRef = collection(db, `artifacts/${appId}/siswa_nilai`);
            // STRICT USER ISOLATION: Only fetch scores managed by this guru
            const q = query(scoresRef, where('guruId', '==', user.uid));

            unsubscribe = onSnapshot(q, (snapshot) => {
                const scoresList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                // Sort client-side
                scoresList.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
                setScores(scoresList);
                console.log(`✅ Scores loaded for user ${user.uid}: ${scoresList.length}`);
            }, (error) => {
                console.error('Error loading scores:', error);
            });
        } catch (error) {
            console.error('Error setting up scores query:', error);
        }

        // Cleanup function
        return () => {
            if (unsubscribe && typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [user]);

    // Load schools
    useEffect(() => {
        if (!user) return;

        let unsubscribe = null;

        try {
            const schoolsRef = collection(db, `artifacts/${appId}/schools`);
            const q = query(schoolsRef);

            unsubscribe = onSnapshot(q, (snapshot) => {
                const schoolsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setSchools(schoolsList);
            }, (error) => {
                console.error('Error loading schools:', error);
            });
        } catch (error) {
            console.error('Error setting up schools query:', error);
        }

        return () => {
            if (unsubscribe && typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [user]);

    // Get student by ID
    const getStudentById = useCallback((studentId) => {
        return students.find(student => student.id === studentId);
    }, [students]);


    // Get assessment type by ID
    const getAssessmentTypeById = useCallback((assessmentTypeId) => {
        return assessmentTypes.find(type => type.id === assessmentTypeId);
    }, [assessmentTypes]);

    // Get student scores
    const getStudentScores = useCallback((studentId) => {
        return scores.filter(score => score.studentId === studentId);
    }, [scores]);

    // Get student progress
    const getStudentProgress = useCallback((studentId) => {
        const studentProgress = {};
        Object.keys(progress).forEach(key => {
            if (key.startsWith(`${studentId}_`)) {
                const competencyId = key.split('_')[1];
                studentProgress[competencyId] = progress[key];
            }
        });
        return studentProgress;
    }, [progress]);

    // 🔧 PERBAIKAN: Update progress function dengan collection global
    const updateProgress = useCallback(async (studentId, competencyId, status, targetGuruId = null) => {
        if (!user) return;

        try {
            // Gunakan collection global untuk integrasi
            const progressRef = collection(db, `artifacts/${appId}/siswa_progress`);
            const progressQuery = query(
                progressRef,
                where('studentId', '==', studentId),
                where('competencyId', '==', competencyId)
            );

            const snapshot = await getDocs(progressQuery);

            // Determine effective guruId
            const activeGuruId = targetGuruId || user.uid;

            if (snapshot.empty) {
                // Create new progress
                await addDoc(progressRef, {
                    studentId,
                    competencyId,
                    status,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    schoolName: user?.namaSekolah || user?.schoolName,
                    guruId: activeGuruId,
                    guruName: user?.displayName || user?.email
                });
            } else {
                // Update existing progress
                const docRef = doc(progressRef, snapshot.docs[0].id);
                // Only update if not already achieved or if status improves (optional logic, but for now simple overwrite)
                await updateDoc(docRef, {
                    status,
                    updatedAt: serverTimestamp(),
                    guruId: activeGuruId // Ensure guruId is consistent
                });
            }
        } catch (error) {
            console.error('Error updating progress:', error);
        }
    }, [user]);

    // 🔧 PERBAIKAN: Update score function dengan collection global
    const updateScore = useCallback(async (studentId, assessmentTypeId, score, feedback) => {
        if (!user) return;

        try {
            // Gunakan collection global untuk integrasi
            const scoresRef = collection(db, `artifacts/${appId}/siswa_nilai`);
            const scoreQuery = query(
                scoresRef,
                where('studentId', '==', studentId),
                where('assessmentTypeId', '==', assessmentTypeId)
            );

            const snapshot = await getDocs(scoreQuery);

            if (snapshot.empty) {
                // Create new score
                await addDoc(scoresRef, {
                    studentId,
                    assessmentTypeId,
                    score,
                    feedback,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    schoolName: user?.namaSekolah || user?.schoolName,
                    guruId: user?.uid,
                    guruName: user?.displayName || user?.email
                });
            } else {
                // Update existing score
                const docRef = doc(scoresRef, snapshot.docs[0].id);
                await updateDoc(docRef, {
                    score,
                    feedback,
                    updatedAt: serverTimestamp()
                });
            }
        } catch (error) {
            console.error('Error updating score:', error);
        }
    }, [user]);

    // 🔧 PERBAIKAN: Function untuk refresh data siswa (manual)
    const refreshStudents = useCallback(async () => {
        if (!user) return;

        try {
            console.log('🔄 StudentDataContext: Manual refresh triggered...');
            setIsLoading(true);
            setStudentsLoaded(false);

            // Trigger reload dengan mengubah dependency
            setStudents([]);

        } catch (error) {
            console.error('❌ StudentDataContext: Error refreshing students:', error);
            setIsLoading(false);
        }
    }, [user]);

    // 🔧 PERBAIKAN: Function untuk membuat data siswa test
    const createTestStudents = useCallback(async () => {
        if (!user || !user.namaSekolah) return;

        try {
            console.log('🔄 StudentDataContext: Creating test students...');
            const schoolName = user.namaSekolah || user.schoolName;
            const adminStudentsRef = collection(db, `artifacts/${appId}/siswa`);

            const testStudents = [
                {
                    nama: 'Ahmad Fadillah',
                    nisn: '12345678',
                    email: 'ahmad@test.com',
                    noTelp: '08123456789',
                    sekolah: schoolName,
                    namaSekolah: schoolName,
                    kelas: 'X',
                    alamat: 'Jl. Test No. 1',
                    tanggalLahir: '2005-01-01',
                    namaOrtu: 'Bapak Ahmad',
                    noTelpOrtu: '08123456788',
                    status: 'active',
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                },
                {
                    nama: 'Siti Nurhaliza',
                    nisn: '87654321',
                    email: 'siti@test.com',
                    noTelp: '08987654321',
                    sekolah: schoolName,
                    namaSekolah: schoolName,
                    kelas: 'X',
                    alamat: 'Jl. Test No. 2',
                    tanggalLahir: '2005-02-02',
                    namaOrtu: 'Ibu Siti',
                    noTelpOrtu: '08987654320',
                    status: 'active',
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                },
                {
                    nama: 'Budi Santoso',
                    nisn: '11223344',
                    email: 'budi@test.com',
                    noTelp: '0811223344',
                    sekolah: schoolName,
                    namaSekolah: schoolName,
                    kelas: 'X',
                    alamat: 'Jl. Test No. 3',
                    tanggalLahir: '2005-03-03',
                    namaOrtu: 'Bapak Budi',
                    noTelpOrtu: '0811223343',
                    status: 'active',
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                }
            ];

            for (const studentData of testStudents) {
                try {
                    await addDoc(adminStudentsRef, studentData);
                    console.log(`✅ Created test student: ${studentData.nama}`);
                } catch (error) {
                    console.error(`❌ Failed to create test student ${studentData.nama}:`, error);
                }
            }

            console.log('✅ Test students created successfully');

            // Refresh data siswa
            refreshStudents();

        } catch (error) {
            console.error('❌ StudentDataContext: Error creating test students:', error);
        }
    }, [user, refreshStudents]);

    // Add assessment type
    const addAssessmentType = useCallback(async (data) => {
        if (!user) return;
        try {
            const assessmentTypesRef = collection(db, `artifacts/${appId}/siswa_assessment_types`);
            await addDoc(assessmentTypesRef, {
                ...data,
                createdBy: user.uid,
                schoolName: user.namaSekolah || user.schoolName || ''
            });
        } catch (error) {
            console.error('Error adding assessment type:', error);
            throw error;
        }
    }, [user]);

    // Update assessment type
    const updateAssessmentType = useCallback(async (id, data) => {
        if (!user) return;
        try {
            const docRef = doc(db, `artifacts/${appId}/siswa_assessment_types`, id);
            await updateDoc(docRef, data);
        } catch (error) {
            console.error('Error updating assessment type:', error);
            throw error;
        }
    }, [user]);

    // Delete assessment type
    const deleteAssessmentType = useCallback(async (id) => {
        if (!user) return;
        try {
            const docRef = doc(db, `artifacts/${appId}/siswa_assessment_types`, id);
            await deleteDoc(docRef);
        } catch (error) {
            console.error('Error deleting assessment type:', error);
            throw error;
        }
    }, [user]);

    const value = {
        // Data
        students,
        assessmentTypes,
        progress,
        scores,
        schools,

        // State
        isLoading,
        error,
        studentsLoaded,

        // Functions
        updateProgress,
        updateScore,
        getStudentById,
        getAssessmentTypeById,
        getStudentScores,
        getStudentProgress,
        refreshStudents,
        createTestStudents,
        addAssessmentType,
        updateAssessmentType,
        deleteAssessmentType,

        // Setters for testing/debugging
        setStudents,
        setAssessmentTypes,
        setProgress,
        setScores,
        setSchools
    };

    return (
        <StudentDataContext.Provider value={value}>
            {children}
        </StudentDataContext.Provider>
    );
};
